import { useActions } from 'ActionsContext';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BreadcrumbsManager } from '../../context/BreadcrumbsManager';
import Contact from '../../models/Contact';
import { InvestmentEntity } from '../../models/InvestmentEntity';
import Investor from '../../models/Investor';
import { MESSAGE_DISMISS_MS } from '../../components/admin/AdminEntityAssociationWizard/adminEntityAssociationWizardActions';
import {
  fetchEntitiesForContact,
  investorDataChanged,
  refreshInvestorDataForInvestor,
} from '../../components/admin/AdminEntityAssociationWizard/adminEntityAssociationWizardData';
import {
  AdminEntityAssociationWizardParams,
  AdminEntityAssociationWizardState,
  WizardStep,
} from '../../components/admin/AdminEntityAssociationWizard/adminEntityAssociationWizardTypes';
import {
  buildAssociateEntityPath,
  decideInitialStep,
  hasRemainingCommitmentFromInvestor,
} from '../../components/admin/AdminEntityAssociationWizard/adminEntityAssociationWizardUtils';
import { useAdminEntityAssociationWizardEffects } from '../../components/admin/AdminEntityAssociationWizard/useAdminEntityAssociationWizardEffects';
import { useAdminEntityAssociationMutations } from '../../components/admin/AdminEntityAssociationWizard/useAdminEntityAssociationMutations';

export function useAdminEntityAssociationWizard({
  investorId,
  selectedEntityId,
  currentState,
}: AdminEntityAssociationWizardParams) {
  const navigate = useNavigate();
  const { refreshActions } = useActions();

  const initialStep: WizardStep = useMemo(
    () => (currentState ? (currentState as WizardStep) : 'lookup'),
    [currentState],
  );

  const [state, setState] = useState<AdminEntityAssociationWizardState>({
    currentStep: initialStep,
    loading: true,
    error: null,
    entities: [],
    selectedEntity: null,
    newEntity: null,
    investor: null,
    contact: null,
    allocatedAmount: undefined,
    validationErrors: {},
    message: undefined,
    currentAllocations: [],
  });

  const setMessage = useCallback((message: { type: 'error' | 'success'; text: string }) => {
    setState((prev) => ({ ...prev, message }));
    setTimeout(() => {
      setState((prev) => ({ ...prev, message: undefined }));
    }, MESSAGE_DISMISS_MS);
  }, []);

  const dismissMessage = useCallback(() => {
    setState((prev) => ({ ...prev, message: undefined }));
  }, []);

  const goToStep = useCallback(
    (step: WizardStep, partial: Partial<AdminEntityAssociationWizardState> = {}) => {
      if (!investorId) return;

      const entityId = partial.selectedEntity?.id ?? state.selectedEntity?.id ?? selectedEntityId;

      navigate(buildAssociateEntityPath(investorId, step, entityId));
      setState((prev) => ({
        ...prev,
        currentStep: step,
        validationErrors: {},
        error: null,
        ...partial,
      }));
    },
    [investorId, navigate, selectedEntityId, state.selectedEntity?.id],
  );

  const refreshInvestorData = useCallback(async () => {
    if (!state.investor) return null;

    const refreshed = await refreshInvestorDataForInvestor(state.investor);
    if (!refreshed) return null;

    setState((prev) => {
      if (
        !investorDataChanged(
          prev.investor,
          refreshed.investor,
          prev.currentAllocations,
          refreshed.allocations,
        )
      ) {
        return prev;
      }

      return {
        ...prev,
        investor: refreshed.investor,
        currentAllocations: refreshed.allocations,
      };
    });

    return refreshed;
  }, [state.investor]);

  const loadEntities = useCallback(
    async (
      contact: Contact | null = state.contact,
      investor: Investor | null = state.investor,
      opts: { navigateOnInit?: boolean; silent?: boolean } = {},
    ) => {
      try {
        if (!contact) {
          if (!opts.silent) setState((prev) => ({ ...prev, loading: false }));
          return;
        }

        if (!opts.silent) setState((prev) => ({ ...prev, loading: true, error: null }));

        const result = await fetchEntitiesForContact(contact, investor);

        setState((prev) => ({
          ...prev,
          entities: result.entities,
          currentAllocations: result.allocations,
          ...(opts.silent ? {} : { loading: false }),
        }));

        if (result.investorTotals) {
          setState((prev) => {
            if (!prev.investor) return prev;
            return {
              ...prev,
              investor: {
                ...prev.investor,
                ...result.investorTotals,
              },
            };
          });
        }

        if (selectedEntityId) {
          const sel =
            result.entities.find((e: InvestmentEntity) => e.id === selectedEntityId) || null;
          if (sel) setState((prev) => ({ ...prev, selectedEntity: sel }));
        }

        if (opts.navigateOnInit) {
          goToStep(decideInitialStep(result.entities));
        }
      } catch (e) {
        console.error(e);
        setState((prev) => ({
          ...prev,
          error: 'An error occurred while processing your request',
          ...(opts.silent ? {} : { loading: false }),
        }));
      }
    },
    [goToStep, selectedEntityId, state.contact, state.investor],
  );

  useAdminEntityAssociationWizardEffects({
    investorId,
    selectedEntityId,
    currentState,
    contact: state.contact,
    investor: state.investor,
    currentStep: state.currentStep,
    loading: state.loading,
    setState,
    loadEntities,
    refreshInvestorData,
  });

  const { handleEntityCreated, associateEntityWithAllocation } = useAdminEntityAssociationMutations(
    {
      state,
      setState,
      setMessage,
      goToStep,
      loadEntities,
      refreshInvestorData,
      refreshActions,
    },
  );

  const handleEntitySelected = useCallback(
    (entity: InvestmentEntity) => {
      setState((prev) => ({ ...prev, selectedEntity: entity }));
      goToStep('allocation', { selectedEntity: entity });
    },
    [goToStep],
  );

  const handleCreateNewEntity = useCallback(() => {
    goToStep('create');
  }, [goToStep]);

  const handleAllocationComplete = useCallback(
    async (allocatedAmount: number) => {
      await refreshInvestorData();
      if (state.selectedEntity) {
        void associateEntityWithAllocation(state.selectedEntity, allocatedAmount);
      }
    },
    [associateEntityWithAllocation, refreshInvestorData, state.selectedEntity],
  );

  const handleContactsComplete = useCallback(async () => {
    const refreshed = await refreshInvestorData();
    const inv = refreshed?.investor ?? state.investor;
    const allocations = refreshed?.allocations ?? state.currentAllocations ?? [];

    if (hasRemainingCommitmentFromInvestor(inv)) {
      await loadEntities(state.contact, inv ?? state.investor, { silent: true });
      goToStep('selection', {
        investor: inv ?? undefined,
        selectedEntity: null,
        currentAllocations: allocations,
        loading: false,
      });
    } else {
      goToStep('complete', {
        investor: inv ?? undefined,
        loading: false,
      });
    }
  }, [
    goToStep,
    loadEntities,
    refreshInvestorData,
    state.contact,
    state.currentAllocations,
    state.investor,
  ]);

  const handleComplete = useCallback(async () => {
    await refreshInvestorData();
    BreadcrumbsManager.clear();
    navigate('/');
  }, [navigate, refreshInvestorData]);

  const handleAllocationBack = useCallback(
    () => goToStep(state.entities.length > 0 ? 'selection' : 'create'),
    [goToStep, state.entities.length],
  );

  const handleCreateCancel = handleAllocationBack;

  const handleLookupComplete = useCallback(() => {
    void loadEntities(state.contact, state.investor);
  }, [loadEntities, state.contact, state.investor]);

  return {
    state,
    dismissMessage,
    handleEntitySelected,
    handleCreateNewEntity,
    handleEntityCreated,
    handleAllocationComplete,
    handleContactsComplete,
    handleComplete,
    handleAllocationBack,
    handleCreateCancel,
    handleLookupComplete,
  };
}
